// -----------------------------------------------------------------------------
// useReminderContent — shared "Reminder & Follow-up" tab content for both
// attended popups (AttendedAppointment / ConsultationAttendedAppointment).
//
// Phase 2 of the popup shell refactor: PURE EXTRACTION of the previously-
// duplicated `reminderTabComponent` IIFE. The returned JSX is byte-identical to
// what each popup rendered before; only the genuine per-popup differences are
// parameterised via `opts`:
//   • mode          — 'gate-first' (AA) | 'post-attended' (CAA). Passed straight
//                     through to AttendedGuidedChecklist(V2); the wrong mode
//                     breaks the checklist fetch strategy.
//   • V1Fallback    — the already-rendered node for the non-tasks-v2 branch
//                     (AA: <FollowUpChecklistV2 .../>, CAA: <FollowUp .../>).
//                     Kept as a node so the hook never needs to know each
//                     popup's distinct fallback prop shape (ref/type vs leadId).
//   • gate props    — AA-only (gateRenderedInHost / externalGateDismissed /
//                     externalRefreshKey / handleNoAttended). Spread into the
//                     guided checklist ONLY when provided, so CAA does not start
//                     passing them (preserves the AA-vs-CAA divergence).
//
// This is a plain function that returns a node and calls no hooks — safe to use
// as `const reminderTabComponent = useReminderContent({...})` inside render.
// -----------------------------------------------------------------------------
import { Box, Button, Typography } from '@mui/material';
import Iconify from '../Iconify';
import { hasFeatureAccess } from '../../utils/featurePermission';
import AttendedGuidedChecklist from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/AttendedGuidedChecklist';
import AttendedGuidedChecklistV2 from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/AttendedGuidedChecklistV2';
import ScheduleTaskReminder from './ScheduleTaskReminder';

export default function useReminderContent({
    // Shared
    practiceId,
    leadId,
    followLead,
    v2GuidedActive,
    useV2Guided,
    showCustomReminder,
    setShowCustomReminder,
    onTaskActivity,
    handleClose,
    handleReloadLeads,
    onOpenSidebarTab,
    // Active treatment — scopes the custom-reminder create + pause-cascade to the
    // treatment the popup is acting on (else pause leaks lead-wide / wrong treatment).
    leadTreatmentId,
    // Per-popup
    mode,
    V1Fallback,
    // Debug helpers (per-popup closures over attDebug)
    dbgSx,
    DbgTag,
    // AA-only gate props (optional)
    gateRenderedInHost,
    externalGateDismissed,
    externalRefreshKey,
    handleNoAttended,
}) {
    const isV2Practice = hasFeatureAccess('tasks-v2', practiceId);

    // ── Non-V2 practice: leave V1 path untouched ──────────────────────────
    if (!isV2Practice) {
        return V1Fallback;
    }

    // ── V2 practice, no template applied: show ScheduleTaskReminder only ──
    if (!v2GuidedActive) {
        return (
            <Box sx={{ ...dbgSx('#FF6D00') }}>
                <DbgTag color="#FF6D00" label="REMINDER" />
                <ScheduleTaskReminder
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={followLead}
                    onTaskActivity={onTaskActivity}
                    handleClose={handleClose}
                    handleReloadLeads={handleReloadLeads}
                    leadTreatmentId={leadTreatmentId}
                />
            </Box>
        );
    }

    // Gate props are AA-only. When `gateRenderedInHost` isn't supplied (CAA) the
    // whole group is omitted so CAA's guided-checklist invocation stays exactly
    // as it was (no gate wiring).
    const gateProps = gateRenderedInHost
        ? { gateRenderedInHost, externalGateDismissed, handleNoAttended }
        : {};

    // ── V2 practice with template: show guided checklist + reminder form ──
    const guidedChecklist = useV2Guided ? (
        <AttendedGuidedChecklistV2
            mode={mode}
            practiceId={practiceId}
            leadId={leadId}
            leadDetail={followLead}
            handleClose={handleClose}
            {...gateProps}
            {...(gateRenderedInHost ? { externalRefreshKey } : {})}
            onTaskActivity={onTaskActivity}
            onOpenSidebarTab={onOpenSidebarTab}
            onCreateReminder={() => setShowCustomReminder(true)}
        />
    ) : (
        <AttendedGuidedChecklist
            mode={mode}
            practiceId={practiceId}
            leadId={leadId}
            leadDetail={followLead}
            handleClose={handleClose}
            {...gateProps}
            handleReloadLeads={handleReloadLeads}
            onTaskActivity={onTaskActivity}
            onCreateReminder={() => setShowCustomReminder(true)}
        />
    );

    return (
        <>
            {!showCustomReminder && (
                <Box sx={{ ...dbgSx('#00BFA5') }}>
                    <DbgTag color="#00BFA5" label="CHECKLIST" />
                    {guidedChecklist}
                </Box>
            )}
            <Box sx={{ ...dbgSx('#FF6D00') }}>
                <DbgTag color="#FF6D00" label="REMINDER" />
                {showCustomReminder ? (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#637381' }}>Custom reminder</Typography>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setShowCustomReminder(false)}
                                startIcon={<Iconify icon="eva:close-fill" width={16} height={16} />}
                                sx={{ color: '#919EAB', textTransform: 'none', fontSize: '12px', minWidth: 0 }}
                            >
                                Remove
                            </Button>
                        </Box>
                        <ScheduleTaskReminder
                            practiceId={practiceId}
                            leadId={leadId}
                            leadDetail={followLead}
                            onTaskActivity={onTaskActivity}
                            handleClose={handleClose}
                            handleReloadLeads={handleReloadLeads}
                            leadTreatmentId={leadTreatmentId}
                        />
                    </Box>
                ) : (
                    <Button
                        variant="text"
                        onClick={() => setShowCustomReminder(true)}
                        startIcon={<Iconify icon="eva:plus-fill" width={18} height={18} />}
                        sx={{ color: '#637381', fontWeight: 600, fontSize: '13px', textTransform: 'none', px: 1, py: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' } }}
                    >
                        Add custom reminder
                    </Button>
                )}
            </Box>
        </>
    );
}
